import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useToast } from '../../hooks/useToast';
import Toast from '../../components/Toast';
import Spinner from '../../components/Spinner';
import './AdminTransactions.css';

function AdminTransactions() {
  const [transactions, setTransactions] = useState([]);
  const [filteredTransactions, setFilteredTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterPaymentMethod, setFilterPaymentMethod] = useState('all');
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [stats, setStats] = useState({
    total_transactions: 0,
    total_revenue: 0,
    successful_transactions: 0,
    failed_transactions: 0,
    refunded_amount: 0
  });
  const navigate = useNavigate();
  const { toast, showToast, closeToast } = useToast();

  useEffect(() => {
    fetchTransactions();
  }, []);

  useEffect(() => {
    applyFilters();
    calculateStats();
  }, [transactions, searchTerm, filterType, filterStatus, filterPaymentMethod]);

  const fetchTransactions = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      if (!token) {
        navigate('/login');
        return;
      }

      const response = await axios.get('/api/admin/transactions', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTransactions(response.data);
      setLoading(false);
    } catch (error) {
      if (error.response?.status === 401) {
        localStorage.removeItem('adminToken');
        navigate('/login');
      } else {
        showToast('Failed to load transactions: ' + (error.response?.data?.error || error.message), 'error');
        setLoading(false);
      }
    }
  };

  const applyFilters = () => {
    let filtered = [...transactions];

    // Search filter
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(t =>
        t.order_number?.toLowerCase().includes(term) ||
        t.customer_email?.toLowerCase().includes(term) ||
        t.customer_name?.toLowerCase().includes(term) ||
        t.payment_intent_id?.toLowerCase().includes(term) ||
        t.charge_id?.toLowerCase().includes(term) ||
        t.card_last4?.includes(term)
      );
    }

    // Type filter
    if (filterType !== 'all') {
      filtered = filtered.filter(t => t.transaction_type === filterType);
    }

    // Status filter
    if (filterStatus !== 'all') {
      filtered = filtered.filter(t => t.status === filterStatus);
    }

    // Payment method filter
    if (filterPaymentMethod !== 'all') {
      filtered = filtered.filter(t => t.payment_method === filterPaymentMethod);
    }

    setFilteredTransactions(filtered);
  };

  const calculateStats = () => {
    const total = transactions.length;
    const successful = transactions.filter(t => t.status === 'succeeded' && t.transaction_type === 'charge').length;
    const failed = transactions.filter(t => t.status === 'failed').length;
    const revenue = transactions
      .filter(t => t.status === 'succeeded' && t.transaction_type === 'charge')
      .reduce((sum, t) => sum + parseFloat(t.amount || 0), 0);
    const refunded = transactions
      .filter(t => t.transaction_type === 'refund' || t.transaction_type === 'partial_refund')
      .reduce((sum, t) => sum + parseFloat(t.amount || 0), 0);

    setStats({
      total_transactions: total,
      total_revenue: revenue,
      successful_transactions: successful,
      failed_transactions: failed,
      refunded_amount: refunded
    });
  };

  const viewTransaction = async (transactionId) => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await axios.get(`/api/admin/transactions/${transactionId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSelectedTransaction(response.data);
      setShowModal(true);
    } catch (error) {
      showToast('Failed to load transaction details: ' + (error.response?.data?.error || error.message), 'error');
    }
  };

  if (loading) return <Spinner text="Loading transactions..." />;

  return (
    <div className="admin-transactions">
      {toast && <Toast message={toast.message} type={toast.type} onClose={closeToast} />}
      <div className="page-header">
        <h1>Transaction History</h1>
      </div>

      <div className="stats-row">
        <div className="stat-box">
          <div className="stat-label">Total Transactions</div>
          <div className="stat-value">{stats.total_transactions}</div>
        </div>
        <div className="stat-box success">
          <div className="stat-label">Total Revenue</div>
          <div className="stat-value">${stats.total_revenue.toFixed(2)}</div>
        </div>
        <div className="stat-box info">
          <div className="stat-label">Successful</div>
          <div className="stat-value">{stats.successful_transactions}</div>
        </div>
        <div className="stat-box danger">
          <div className="stat-label">Failed</div>
          <div className="stat-value">{stats.failed_transactions}</div>
        </div>
        <div className="stat-box warning">
          <div className="stat-label">Refunded</div>
          <div className="stat-value">${stats.refunded_amount.toFixed(2)}</div>
        </div>
      </div>

      <div className="filters-section">
        <input
          type="text"
          placeholder="Search by order, email, name, payment ID, or last 4 digits..."
          className="search-input"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />

        <div className="filters-row">
          <select
            className="filter-select"
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
          >
            <option value="all">All Types</option>
            <option value="charge">Charge</option>
            <option value="refund">Refund</option>
            <option value="partial_refund">Partial Refund</option>
            <option value="failed">Failed</option>
          </select>

          <select
            className="filter-select"
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
          >
            <option value="all">All Status</option>
            <option value="succeeded">Succeeded</option>
            <option value="failed">Failed</option>
            <option value="pending">Pending</option>
            <option value="refunded">Refunded</option>
            <option value="partially_refunded">Partially Refunded</option>
          </select>

          <select
            className="filter-select"
            value={filterPaymentMethod}
            onChange={(e) => setFilterPaymentMethod(e.target.value)}
          >
            <option value="all">All Payment Methods</option>
            <option value="card">Card</option>
            <option value="paypal">PayPal</option>
            <option value="unknown">Unknown</option>
          </select>
        </div>

        <div className="results-count">
          Showing {filteredTransactions.length} of {transactions.length} transactions
        </div>
      </div>

      <div className="transactions-table-container">
        <table className="transactions-table">
          <thead>
            <tr>
              <th>ID</th>
              <th>Date</th>
              <th>Order</th>
              <th>Customer</th>
              <th>Type</th>
              <th>Amount</th>
              <th>Payment Method</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredTransactions.map(transaction => (
              <tr key={transaction.id}>
                <td>{transaction.id}</td>
                <td className="date-cell">
                  {new Date(transaction.created_at).toLocaleString()}
                </td>
                <td>
                  {transaction.order_number ? (
                    <span className="order-link">
                      {transaction.order_number}
                    </span>
                  ) : (
                    '-'
                  )}
                </td>
                <td>
                  <div className="customer-info">
                    <div className="customer-name">{transaction.customer_name || '-'}</div>
                    <div className="customer-email">{transaction.customer_email || '-'}</div>
                  </div>
                </td>
                <td>
                  <span className={`badge badge-type ${transaction.transaction_type}`}>
                    {transaction.transaction_type}
                  </span>
                </td>
                <td className="amount-cell">
                  <span className={transaction.transaction_type.includes('refund') ? 'refund-amount' : ''}>
                    {transaction.transaction_type.includes('refund') ? '-' : ''}${parseFloat(transaction.amount || 0).toFixed(2)}
                  </span>
                  <div className="currency">{(transaction.currency || 'usd').toUpperCase()}</div>
                </td>
                <td>
                  {transaction.payment_method && (
                    <div className="payment-info">
                      <div className="payment-method">{transaction.payment_method}</div>
                      {transaction.card_brand && (
                        <div className="card-info">
                          {transaction.card_brand} •••• {transaction.card_last4}
                        </div>
                      )}
                    </div>
                  )}
                  {!transaction.payment_method && '-'}
                </td>
                <td>
                  <span className={`badge badge-status ${transaction.status}`}>
                    {transaction.status}
                  </span>
                </td>
                <td>
                  <button
                    className="btn-action btn-view"
                    onClick={() => viewTransaction(transaction.id)}
                  >
                    View Details
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {filteredTransactions.length === 0 && (
          <div className="no-results">
            No transactions found matching your filters.
          </div>
        )}
      </div>

      {/* Transaction Details Modal */}
      {showModal && selectedTransaction && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Transaction Details</h2>
              <button className="modal-close" onClick={() => setShowModal(false)}>×</button>
            </div>
            <div className="transaction-details">
              <div className="detail-row">
                <span className="detail-label">Transaction ID:</span>
                <span className="detail-value">{selectedTransaction.id}</span>
              </div>
              <div className="detail-row">
                <span className="detail-label">Date:</span>
                <span className="detail-value">{new Date(selectedTransaction.created_at).toLocaleString()}</span>
              </div>
              <div className="detail-row">
                <span className="detail-label">Order Number:</span>
                <span className="detail-value">{selectedTransaction.order_number || 'N/A'}</span>
              </div>
              <div className="detail-row">
                <span className="detail-label">Type:</span>
                <span className="detail-value">
                  <span className={`badge badge-type ${selectedTransaction.transaction_type}`}>
                    {selectedTransaction.transaction_type}
                  </span>
                </span>
              </div>
              <div className="detail-row">
                <span className="detail-label">Status:</span>
                <span className="detail-value">
                  <span className={`badge badge-status ${selectedTransaction.status}`}>
                    {selectedTransaction.status}
                  </span>
                </span>
              </div>
              <div className="detail-row">
                <span className="detail-label">Amount:</span>
                <span className="detail-value amount-large">
                  ${parseFloat(selectedTransaction.amount || 0).toFixed(2)} {(selectedTransaction.currency || 'usd').toUpperCase()}
                </span>
              </div>
              <div className="detail-row">
                <span className="detail-label">Customer Name:</span>
                <span className="detail-value">{selectedTransaction.customer_name || 'N/A'}</span>
              </div>
              <div className="detail-row">
                <span className="detail-label">Customer Email:</span>
                <span className="detail-value">{selectedTransaction.customer_email || 'N/A'}</span>
              </div>
              <div className="detail-row">
                <span className="detail-label">Payment Method:</span>
                <span className="detail-value">{selectedTransaction.payment_method || 'N/A'}</span>
              </div>
              {selectedTransaction.card_brand && (
                <div className="detail-row">
                  <span className="detail-label">Card:</span>
                  <span className="detail-value">
                    {selectedTransaction.card_brand} ending in {selectedTransaction.card_last4}
                  </span>
                </div>
              )}
              <div className="detail-row">
                <span className="detail-label">Payment Intent ID:</span>
                <span className="detail-value mono">{selectedTransaction.payment_intent_id || 'N/A'}</span>
              </div>
              <div className="detail-row">
                <span className="detail-label">Charge ID:</span>
                <span className="detail-value mono">{selectedTransaction.charge_id || 'N/A'}</span>
              </div>
              {selectedTransaction.refund_id && (
                <div className="detail-row">
                  <span className="detail-label">Refund ID:</span>
                  <span className="detail-value mono">{selectedTransaction.refund_id}</span>
                </div>
              )}
              {selectedTransaction.error_message && (
                <div className="detail-row error">
                  <span className="detail-label">Error Message:</span>
                  <span className="detail-value">{selectedTransaction.error_message}</span>
                </div>
              )}
              {selectedTransaction.shipping_address && (
                <div className="detail-row">
                  <span className="detail-label">Shipping Address:</span>
                  <span className="detail-value">
                    {selectedTransaction.shipping_address}, {selectedTransaction.shipping_city}, {selectedTransaction.shipping_state} {selectedTransaction.shipping_zip}, {selectedTransaction.shipping_country}
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminTransactions;
